import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { surgeries, specialties, locations } from "@/lib/healic-data";
import { SurgeryLocationView } from "@/components/SurgeryLocationView";
import { getCityIntro, getCitySections, getCityFaqs } from "@/lib/surgery-city-content";
import { Check } from "lucide-react";

const SITE_URL = "https://healic.in";

function shortCity(city: string): string {
  return city.split(",")[0]!.trim();
}

export const Route = createFileRoute("/surgeries/$slug/$location")({
  loader: ({ params }) => {
    const surgery = surgeries.find((s) => s.slug === params.slug);
    const location = locations.find((l) => l.slug === params.location);
    if (!surgery || !location) throw notFound();
    const specialty = specialties.find((sp) => sp.slug === surgery.specialtySlug);
    const related = surgeries
      .filter((s) => s.specialtySlug === surgery.specialtySlug && s.slug !== surgery.slug)
      .slice(0, 4);
    return { surgery, specialty, related, location };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.surgery;
    const l = loaderData?.location;
    if (!s || !l) return { meta: [{ title: "Surgery — Healic" }] };
    const city = shortCity(l.city);
    const title = `${s.name} in ${city} — Cost, Best Doctors & Recovery | Healic`;
    const description = `Looking for ${s.name.toLowerCase()} in ${city}? ${s.short} Fixed-price packages, expert surgeons & insurance support at Healic ${city}.`;
    const path = `/surgeries/${s.slug}/${l.slug}`;
    const url = `${SITE_URL}${path}`;
    // Self-referencing canonical: each city page owns its own URL.
    const canonicalUrl = url;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "geo.placename", content: city },
        { property: "og:title", content: `${s.name} in ${city} — Healic` },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: "Healic Multispeciality Hospital" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${s.name} in ${city} — Healic` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: `${s.name} in ${city}`,
            description: s.overview,
            procedureType: "https://schema.org/SurgicalProcedure",
            howPerformed: s.procedure.join(". "),
            preparation: "Pre-operative consultation, blood work and anaesthesia review.",
            followup: s.recovery,
            url,
            location: { "@type": "Hospital", name: `Healic ${city}`, address: l.address },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Surgeries", item: `${SITE_URL}/surgeries` },
              { "@type": "ListItem", position: 2, name: s.name, item: `${SITE_URL}/surgeries/${s.slug}` },
              { "@type": "ListItem", position: 3, name: city, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: s.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Page not found</h1>
        <Link to="/surgeries" className="mt-6 inline-block text-accent">Browse all surgeries →</Link>
      </div>
    </PageShell>
  ),
  component: SurgeryLocationPage,
});

function SurgeryLocationPage() {
  const { surgery, specialty, related, location } = Route.useLoaderData();
  const cityIntro = getCityIntro(surgery, location);
  const citySections = getCitySections(surgery, location);
  const cityFaqs = getCityFaqs(surgery, location);
  const surgeryWithCityFaqs = cityFaqs.length
    ? { ...surgery, faqs: [...cityFaqs, ...surgery.faqs] }
    : surgery;
  const extraSections =
    citySections.length > 0 ? (
      <div className="space-y-12">
        {citySections.map((s) => (
          <div key={s.heading}>
            <h2 className="font-display text-2xl font-bold">{s.heading}</h2>
            <p className="mt-3 text-muted-foreground">{s.body}</p>
            {s.bullets && (
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 rounded-2xl bg-cream p-4">
                    <Check className="mt-0.5 h-5 w-5 text-accent" />
                    <span className="text-sm font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    ) : null;
  return (
    <SurgeryLocationView
      surgery={surgeryWithCityFaqs}
      specialty={specialty}
      related={related}
      location={location}
      cityIntro={cityIntro}
      extraSections={extraSections}
    />
  );
}

